import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const fetchCategories = createAsyncThunk(
  "categories/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/quiz/categories");
      return response.data.data; // Returns hierarchical structure
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch categories"
      );
    }
  }
);

export const createCategory = createAsyncThunk(
  "category/createCategory",
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/quiz/categories", categoryData);
      const data = response.data?.data || response.data;
      return data?.category || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create category"
      );
    }
  }
);

export const updateCategory = createAsyncThunk(
  "category/updateCategory",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/quiz/categories/${id}`, data);
      const responseData = response.data?.data || response.data;
      return responseData?.category || responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update category"
      );
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "category/deleteCategory",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/quiz/categories/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete category"
      );
    }
  }
);

export const fetchCategoryQuestions = createAsyncThunk(
  "category/fetchCategoryQuestions",
  async ({ categoryId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin/quiz/categories/${categoryId}/questions`,
        { params }
      );
      return {
        questions: response.data.data || [],
        pagination: response.data.pagination || null,
        category: response.data.category || null,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch category questions"
      );
    }
  }
);

export const attachQuestionsToCategory = createAsyncThunk(
  "category/attachQuestions",
  async ({ categoryId, questionIds }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin/quiz/categories/${categoryId}/questions`,
        { question_ids: questionIds }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to map questions"
      );
    }
  }
);

export const detachQuestionFromCategory = createAsyncThunk(
  "category/detachQuestion",
  async ({ categoryId, questionId }, { rejectWithValue }) => {
    try {
      await api.delete(
        `/admin/quiz/categories/${categoryId}/questions/${questionId}`
      );
      return { categoryId, questionId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to unmap question"
      );
    }
  }
);

const categorySlice = createSlice({
  name: "category",
  initialState: {
    categories: [], // Hierarchical structure with children
    loading: false,
    error: null,
    categoryQuestions: [],
    categoryQuestionsLoading: false,
    categoryQuestionsPagination: null,
    activeCategoryMeta: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCategoryQuestions: (state) => {
      state.categoryQuestions = [];
      state.categoryQuestionsPagination = null;
      state.activeCategoryMeta = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex(
          (cat) => cat.id === action.payload.id
        );
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter(
          (cat) => cat.id !== action.payload
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Category mapped questions
      .addCase(fetchCategoryQuestions.pending, (state) => {
        state.categoryQuestionsLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryQuestions.fulfilled, (state, action) => {
        state.categoryQuestionsLoading = false;
        state.categoryQuestions = action.payload.questions;
        state.categoryQuestionsPagination = action.payload.pagination;
        state.activeCategoryMeta = action.payload.category;
      })
      .addCase(fetchCategoryQuestions.rejected, (state, action) => {
        state.categoryQuestionsLoading = false;
        state.error = action.payload;
      })
      .addCase(detachQuestionFromCategory.fulfilled, (state, action) => {
        state.categoryQuestions = state.categoryQuestions.filter(
          (q) => q.id !== action.payload.questionId
        );
        if (state.categoryQuestionsPagination) {
          state.categoryQuestionsPagination = {
            ...state.categoryQuestionsPagination,
            total: Math.max(
              0,
              (state.categoryQuestionsPagination.total || 0) - 1
            ),
          };
        }
      });
  },
});

export const { clearError, clearCategoryQuestions } = categorySlice.actions;

// Helper selector to get flat list of all categories
export const selectFlatCategories = (state) => {
  const flatten = (categories, level = 0) => {
    const flat = [];
    categories.forEach((cat) => {
      flat.push({ ...cat, level });
      if (cat.children && cat.children.length > 0) {
        flat.push(...flatten(cat.children, level + 1));
      }
    });
    return flat;
  };
  return flatten(state.category.categories);
};

export default categorySlice.reducer;
